import {
	Box,
	Button,
	Chip,
	Collapse,
	Dialog,
	Grid,
	IconButton,
	DialogContent,
	DialogTitle,
	Divider,
	Rating,
	Stack,
	Table,
	TableBody,
	TableCell,
	TableContainer,
	TableHead,
	TableRow,
	TableSortLabel,
	TextField,
	Tooltip,
	Typography,
} from '@mui/material';
import { Fragment, useState } from 'react';
import { FaChevronDown, FaChevronUp } from 'react-icons/fa';
import { useNotify } from 'react-admin';

// GH-697: the identified offers table (+ its feedback / reason popups) is shared between the
// per-company budget tab (SubsidyManagement) and the cross-company page (AllSubsidyOffers).

export type OfferStatus = 'offered' | 'accepted' | 'declined' | 'expired' | 'cancelled';

export interface OfferRow {
	/** GH-697: populated on the cross-company page */
	company?: { _id: string; english_name?: string } | string;
	_id: string;
	member?: { _id: string; first_name?: string; last_name?: string; email?: string; department?: string };
	intervention_category: 'intro' | 'full_treatment' | 'series';
	/** GH-674: the offer carried the "ייתכן המשך" copy (reserve-backed potential). */
	adaptive_track?: boolean;
	/** GH-674: this IS a continuation offer, funded by the reserve bucket. */
	continuation_of?: string;
	price: number;
	status: OfferStatus;
	offered_at: string;
	offer_expires_at: string;
	accepted_at?: string;
	/** First click on the offer link from the email/WhatsApp notification */
	clicked_at?: string;
	click_channel?: 'em' | 'wa' | 'em_rem' | 'wa_rem';
	/** Set once by the hourly expiry-reminder cron (email+push+WhatsApp) */
	reminder_sent_at?: string;
	cycle_year: number;
	selected_expert?: { _id: string; name?: string; english_name?: string };
	/** "תאריך המענה" — when the booked session was scheduled for (absent if not booked / cancelled). */
	session_time?: string;
	need_score_at_offer?: number;
	/** Submitted session feedback (post_session + optional 21-day follow-up). */
	feedback?: FeedbackRow[];
	/** Session already happened (≥3h ago) but no feedback was left. */
	awaiting_feedback?: boolean;
	/** Link to (re)send the feedback request — present when awaiting_feedback. */
	feedback_link?: string | null;
	/** GH-693: the automatic 48h feedback reminder was sent (email + WhatsApp). */
	feedback_reminder_sent_at?: string;
	/** GH-730: the expert's own post-session survey (submitted only). */
	expert_feedback?: ExpertFeedbackRow | null;
	/** GH-727: headline A/B — 'A' | 'B' drawn at creation; which client build showed it. */
	copy_variant?: string;
	copy_experiment?: string;
	/** GH-796: a demo offer an admin sent to show HR what employees get — never counted in budgets/aggregates. */
	is_demo?: boolean;
	/** GH-796: the admin who sent the demo (raw id, not populated). */
	demo_sent_by?: string | null;
	shown_app_version?: string;
	shown_platform?: string;
	/** GH-660: privacy-safe HR-facing rationale for the allocation. */
	hr_allocation_note_he?: string;
	hr_allocation_note_en?: string;
	hr_note_approx?: boolean;
	/** GH-660: matcher's reason WHY this expert was offered (selected expert, or the first/recommended if not booked). */
	/** GH-660 intake — the member's own "what to focus on" note from booking (admin-only). */
	focus_topic?: string;
	offer_reason_he?: string;
	offer_reason_en?: string;
	offer_reason_expert?: string;
	offer_reason_source?: 'selected' | 'recommended';
	reallocation_depth?: number;
	/** The offer this one's budget was reallocated from (previous member declined/expired) */
	reallocated_from?: {
		_id: string;
		status?: OfferStatus;
		member?: { _id: string; first_name?: string; last_name?: string };
	};
}

export const STATUS_LABELS: Record<OfferStatus, string> = {
	offered: 'בהמתנה',
	accepted: 'התקבלה',
	declined: 'נדחתה',
	expired: 'פקעה',
	cancelled: 'בוטלה',
};

export const STATUS_COLORS: Record<OfferStatus, 'default' | 'primary' | 'success' | 'warning' | 'error' | 'info'> = {
	offered: 'warning',
	accepted: 'success',
	declined: 'error',
	expired: 'default',
	cancelled: 'default',
};

export const TIER_LABELS: Record<'intro' | 'full_treatment' | 'series', string> = {
	// Renamed per Uri (2026-09-02) to match the member-facing tier language.
	// GH-743 names
	intro: 'פגישה ממוקדת עם מומחה',
	full_treatment: 'פגישה מלאה עם מומחה',
	series: 'סדרת ייעוץ',
};

export interface FeedbackRow {
	kind: 'post_session' | 'followup_21d';
	rating?: number;
	answers?: { question: string; answer: string }[];
	comment?: string;
	submitted_at?: string;
}

/** GH-730 — the expert's closed survey; `further_need` is the engine signal (none/low/medium/high/unknown). */
export interface ExpertFeedbackRow {
	answers?: { key: string; question: string; value: string; answer: string }[];
	further_need?: string;
	need_reason?: string;
	submitted_at?: string;
}

/** GH-730 — Q5 values → short admin labels. */
export const EXPERT_NEED_LABELS: Record<string, string> = {
	none: 'אין צורך',
	low: 'צורך נמוך',
	medium: 'צורך בינוני',
	high: 'צורך גבוה',
	unknown: 'לא ניתן להעריך',
};

export const FEEDBACK_KIND_LABELS: Record<FeedbackRow['kind'], string> = {
	post_session: 'משוב לאחר השיחה',
	followup_21d: 'מעקב 21 יום',
};

interface Props {
	offers: OfferRow[];
	/** Show a leading "חברה" column (cross-company page). */
	showCompany?: boolean;
	/** Cap rendered rows (per-company tab keeps 200). Omit = render all. */
	maxRows?: number;
	emptyText?: string;
	/**
	 * 'full' (default, the company tab): every field is a column.
	 * 'compact' (GH-697 cross-company page): 9 scannable columns + a chevron that opens a
	 * full-width detail panel under the row with the rest (long texts read as paragraphs).
	 */
	layout?: 'full' | 'compact';
}

const CHANNEL_LABELS: Record<string, string> = { em: 'מייל', wa: 'וואטסאפ', em_rem: 'מייל תזכורת', wa_rem: 'וואטסאפ תזכורת' };

// GH-796: marks a demo offer (sent by an admin to show HR) wherever a row can appear.
const DemoChip = () => (
	<Tooltip title="הקצאת הדגמה שנשלחה מהאדמין — לא נספרת בתקציב, במחזור או בסיכומים">
		<Chip size="small" label="הדגמה" sx={{ mr: 0.5, bgcolor: '#FCE7F3', color: '#9D174D', fontWeight: 700, fontSize: 11, height: 20 }} />
	</Tooltip>
);

const fmtDate = (d?: string) => (d ? new Date(d).toLocaleDateString('he-IL') : '—');
const fmtDateTime = (d?: string) =>
	d
		? new Date(d).toLocaleString('he-IL', {
				day: 'numeric',
				month: 'numeric',
				year: 'numeric',
				hour: '2-digit',
				minute: '2-digit',
				hour12: false,
				timeZone: 'Asia/Jerusalem',
		  })
		: '—';

const SubsidyOffersTable = ({
	offers,
	showCompany = false,
	maxRows,
	emptyText = 'אין הצעות בסטטוס זה.',
	layout = 'full',
}: Props) => {
	const notify = useNotify();
	// Feedback popup (null = closed). `items` = submitted feedback to display;
	// `link` (mode) = the offer has no feedback yet → show the (re)send link instead.
	const [feedbackView, setFeedbackView] = useState<
		{ name: string; items?: FeedbackRow[]; link?: string | null; expert?: ExpertFeedbackRow | null } | null
	>(null);
	// GH-660: "why this expert was offered" popup (null = closed).
	const [noteView, setNoteView] = useState<
		{ name: string; he?: string; en?: string; expert?: string; source?: 'selected' | 'recommended' } | null
	>(null);
	const rows = maxRows ? offers.slice(0, maxRows) : offers;
	const colSpan = showCompany ? 18 : 17;
	// compact layout: which rows are open (by offer id)
	const [open, setOpen] = useState<Record<string, boolean>>({});
	const allOpen = rows.length > 0 && rows.every((o) => open[o._id]);
	const toggleAll = () => setOpen(allOpen ? {} : Object.fromEntries(rows.map((o) => [o._id, true])));

	const memberNameOf = (o: OfferRow) =>
		o.member ? `${o.member.first_name ?? ''} ${o.member.last_name ?? ''}`.trim() || '—' : '—';

	// Shared cell renderers (compact layout). The full layout below keeps its original inline JSX.
	// GH-730: small chip next to the member's feedback — the expert answered too.
	const expertChip = (o: OfferRow) =>
		o.expert_feedback ? (
			<Tooltip title="המומחה מילא משוב — צפייה">
				<Chip
					size="small"
					variant="outlined"
					color="primary"
					clickable
					sx={{ ml: 0.5 }}
					onClick={() =>
						setFeedbackView({
							name: memberNameOf(o),
							items: o.feedback || [],
							link: o.awaiting_feedback ? o.feedback_link ?? null : null,
							expert: o.expert_feedback,
						})
					}
					label={`מומחה${o.expert_feedback.further_need ? ` · ${EXPERT_NEED_LABELS[o.expert_feedback.further_need] ?? ''}` : ''}`}
				/>
			</Tooltip>
		) : null;
	const feedbackCell = (o: OfferRow) => (
		<>
			{memberFeedbackCell(o)}
			{expertChip(o)}
		</>
	);
	const memberFeedbackCell = (o: OfferRow) => {
		const memberName = memberNameOf(o);
		if (o.feedback && o.feedback.length > 0)
			return (
				<Tooltip title="צפייה במשוב">
					<Chip
						size="small"
						color="secondary"
						clickable
						onClick={() => setFeedbackView({ name: memberName, items: o.feedback!, expert: o.expert_feedback })}
						label={`★ ${o.feedback[0].rating ?? '—'}/5${o.feedback.length > 1 ? ` (+${o.feedback.length - 1})` : ''}`}
					/>
				</Tooltip>
			);
		if (o.awaiting_feedback)
			return (
				<Tooltip title={o.feedback_reminder_sent_at ? 'הפגישה התקיימה, אין משוב, תזכורת אוטומטית נשלחה — קישור למשוב' : 'הפגישה התקיימה — קישור למשוב'}>
					<Chip
						size="small"
						color="warning"
						variant="outlined"
						clickable
						onClick={() => setFeedbackView({ name: memberName, link: o.feedback_link ?? null, expert: o.expert_feedback })}
						label={o.feedback_reminder_sent_at ? `אין משוב · תזכורת ${fmtDate(o.feedback_reminder_sent_at)}` : 'אין משוב'}
					/>
				</Tooltip>
			);
		return '—';
	};
	const reallocatedLabel = (o: OfferRow) => {
		const prev = o.reallocated_from?.member;
		const from = prev
			? `${prev.first_name ?? ''} ${prev.last_name ?? ''}`.trim() || 'הצעה קודמת'
			: o.reallocated_from
				? 'הצעה קודמת'
				: null;
		return from ? `↩ ${from}${(o.reallocation_depth ?? 0) > 1 ? ` (${o.reallocation_depth})` : ''}` : null;
	};

	const Field = ({ label, children }: { label: string; children: React.ReactNode }) => (
		<Box>
			<Typography variant="caption" color="text.secondary" display="block">
				{label}
			</Typography>
			<Typography variant="body2" component="div">
				{children ?? '—'}
			</Typography>
		</Box>
	);

	const feedbackDialog = (
		<>
			{/* Feedback popup — opens from the "משוב" chip in the offers table. */}
			<Dialog
				open={!!feedbackView}
				onClose={() => setFeedbackView(null)}
				maxWidth="sm"
				fullWidth
				dir="rtl"
			>
				<DialogTitle>
					{feedbackView?.items?.length ? 'משוב' : feedbackView?.expert ? 'משוב המומחה' : 'לא הושאר משוב'} — {feedbackView?.name}
				</DialogTitle>
				<DialogContent>
					{/* Link mode: session happened, no feedback yet — show the (re)send link. */}
					{feedbackView && !feedbackView.items?.length && (
						<Stack spacing={1.5} sx={{ pt: 1 }}>
							{feedbackView.link ? (
								<>
									<Typography variant="body2" color="text.secondary">
										הפגישה כבר התקיימה אך העובד לא מילא משוב. אפשר לשלוח לו שוב את הקישור:
									</Typography>
									<TextField
										value={feedbackView.link}
										size="small"
										fullWidth
										InputProps={{ readOnly: true }}
										onFocus={(e) => e.target.select()}
									/>
									<Box>
										<Button
											variant="outlined"
											size="small"
											onClick={() => {
												navigator.clipboard?.writeText(feedbackView.link || '');
												notify('הקישור הועתק', { type: 'info' });
											}}
										>
											העתקת קישור
										</Button>
									</Box>
								</>
							) : (
								<Typography variant="body2" color="text.secondary">
									בקשת המשוב עדיין לא נשלחה (הקישור נוצר אוטומטית כ־10 דקות לאחר הפגישה).
								</Typography>
							)}
						</Stack>
					)}
					<Stack spacing={2.5} sx={{ pt: 1 }}>
						{feedbackView?.items?.map((fb, idx) => (
							<Box key={idx}>
								<Stack direction="row" spacing={1.5} alignItems="center" sx={{ mb: 1 }}>
									<Chip size="small" color="secondary" label={FEEDBACK_KIND_LABELS[fb.kind]} />
									{typeof fb.rating === 'number' && (
										<Rating value={fb.rating} max={5} readOnly size="small" />
									)}
									{fb.submitted_at && (
										<Typography variant="caption" color="text.secondary">
											{new Date(fb.submitted_at).toLocaleDateString('he-IL')}
										</Typography>
									)}
								</Stack>
								{fb.answers?.map((a, i) => (
									<Box key={i} sx={{ mb: 1 }}>
										<Typography variant="body2" sx={{ fontWeight: 600 }}>
											{a.question}
										</Typography>
										<Typography variant="body2" color="text.secondary">
											{a.answer || '—'}
										</Typography>
									</Box>
								))}
								{/* `comment` is a copy of the open-question answer (kept for the HR quote), so it is
								    already shown in the answers list above — show it only when it differs (Udi, 9.9.2026). */}
								{fb.comment && !(fb.answers || []).some((a: any) => (a.answer || '').trim() === fb.comment!.trim()) && (
									<Box sx={{ mt: 1 }}>
										<Typography variant="body2" sx={{ fontWeight: 600 }}>
											הערה חופשית
										</Typography>
										<Typography variant="body2" color="text.secondary">
											{fb.comment}
										</Typography>
									</Box>
								)}
								{idx < (feedbackView?.items?.length ?? 0) - 1 && <Divider sx={{ mt: 2 }} />}
							</Box>
						))}
						{/* GH-730 — the expert's own survey, next to the member's feedback */}
						{feedbackView?.expert && (
							<Box>
								{(feedbackView.items?.length ?? 0) > 0 && <Divider sx={{ mb: 2 }} />}
								<Stack direction="row" spacing={1.5} alignItems="center" sx={{ mb: 1 }}>
									<Chip size="small" color="primary" label="משוב המומחה" />
									{feedbackView.expert.further_need && (
										<Chip
											size="small"
											variant="outlined"
											label={`צורך בהמשך: ${EXPERT_NEED_LABELS[feedbackView.expert.further_need] ?? feedbackView.expert.further_need}`}
										/>
									)}
									{feedbackView.expert.submitted_at && (
										<Typography variant="caption" color="text.secondary">
											{new Date(feedbackView.expert.submitted_at).toLocaleDateString('he-IL')}
										</Typography>
									)}
								</Stack>
								{feedbackView.expert.answers?.map((a) => (
									<Box key={a.key} sx={{ mb: 1 }}>
										<Typography variant="body2" sx={{ fontWeight: 600 }}>
											{a.question}
										</Typography>
										<Typography variant="body2" color="text.secondary">
											{/^[1-5]$/.test(a.value) ? `${a.value} מתוך 5 — ${a.answer}` : a.answer}
										</Typography>
									</Box>
								))}
							</Box>
						)}
					</Stack>
				</DialogContent>
			</Dialog>

			{/* GH-660 (admin): why this expert was offered — the matcher's fit reason. */}
			<Dialog open={!!noteView} onClose={() => setNoteView(null)} maxWidth="sm" fullWidth dir="rtl">
				<DialogTitle>סיבת ההצעה — {noteView?.name}</DialogTitle>
				<DialogContent>
					<Stack spacing={2} sx={{ pt: 1 }}>
						<Typography variant="caption" color="text.secondary">
							{noteView?.source === 'selected'
								? 'הנימוק מתייחס למומחה שהעובד/ת בחר/ה ופגישה נקבעה.'
								: 'טרם נקבעה פגישה — הנימוק מתייחס למומחה המומלץ (הראשון בדירוג ההתאמה).'}
						</Typography>
						{noteView?.expert && (
							<Box>
								<Typography variant="body2" sx={{ fontWeight: 600, mb: 0.5 }}>
									מומחה
								</Typography>
								<Typography variant="body2" color="text.secondary">
									{noteView.expert}
									{noteView.source === 'selected' ? ' (נבחר)' : ' (מומלץ)'}
								</Typography>
							</Box>
						)}
						<Box>
							<Typography variant="body2" sx={{ fontWeight: 600, mb: 0.5 }}>
								עברית
							</Typography>
							<Typography variant="body2" color="text.secondary">
								{noteView?.he || '—'}
							</Typography>
						</Box>
						{noteView?.en && (
							<Box dir="ltr">
								<Typography variant="body2" sx={{ fontWeight: 600, mb: 0.5 }}>
									English
								</Typography>
								<Typography variant="body2" color="text.secondary">
									{noteView.en}
								</Typography>
							</Box>
						)}
					</Stack>
				</DialogContent>
			</Dialog>
		</>
	);

	// compact layout: column sorting (client-side, on the rows this table received)
	type SortKey = 'company' | 'offered_at' | 'member' | 'tier' | 'price' | 'status' | 'expert' | 'session_time' | 'feedback';
	const [sortKey, setSortKey] = useState<SortKey>('offered_at');
	const [sortDir, setSortDir] = useState<'asc' | 'desc'>('desc');
	const onSort = (key: SortKey) => {
		if (key === sortKey) setSortDir((d) => (d === 'asc' ? 'desc' : 'asc'));
		else {
			setSortKey(key);
			// dates/numbers default to newest/highest first, texts to א-ב
			setSortDir(['offered_at', 'session_time', 'price', 'feedback'].includes(key) ? 'desc' : 'asc');
		}
	};
	const STATUS_ORDER: Record<OfferStatus, number> = { offered: 0, accepted: 1, declined: 2, expired: 3, cancelled: 4 };
	const sortVal = (o: OfferRow, key: SortKey): string | number => {
		switch (key) {
			case 'company':
				return (typeof o.company === 'object' && o.company?.english_name) || '';
			case 'offered_at':
				return o.offered_at ? new Date(o.offered_at).getTime() : 0;
			case 'member':
				return memberNameOf(o);
			case 'tier':
				return o.continuation_of ? 'המשך טיפול' : TIER_LABELS[o.intervention_category] || '';
			case 'price':
				return o.price ?? 0;
			case 'status':
				return STATUS_ORDER[o.status] ?? 9;
			case 'expert':
				return o.selected_expert?.name || o.selected_expert?.english_name || '';
			case 'session_time':
				return o.session_time ? new Date(o.session_time).getTime() : 0;
			case 'feedback':
				// rating first; "awaiting feedback" rows just above the empty ones
				return o.feedback?.length ? (o.feedback[0].rating ?? 0) + 1 : o.awaiting_feedback ? 0.5 : 0;
		}
	};
	const sortedRows = [...rows].sort((a, b) => {
		const va = sortVal(a, sortKey), vb = sortVal(b, sortKey);
		const c = typeof va === 'number' && typeof vb === 'number' ? va - vb : String(va).localeCompare(String(vb), 'he');
		// empty values always sink to the bottom regardless of direction
		const ea = va === '' || va === 0, eb = vb === '' || vb === 0;
		if (ea !== eb) return ea ? 1 : -1;
		return sortDir === 'asc' ? c : -c;
	});
	const SortHead = ({ k, label }: { k: SortKey; label: string }) => (
		<TableCell align="right" sortDirection={sortKey === k ? sortDir : false} sx={{ whiteSpace: 'nowrap' }}>
			<TableSortLabel active={sortKey === k} direction={sortKey === k ? sortDir : 'asc'} onClick={() => onSort(k)}>
				{label}
			</TableSortLabel>
		</TableCell>
	);

	if (layout === 'compact') {
		const compactColSpan = (showCompany ? 10 : 9) + 2; // +1 GH-727 headline column
		return (
			<>
				<Stack direction="row" justifyContent="flex-end" sx={{ mb: 1 }}>
					<Button size="small" onClick={toggleAll} disabled={rows.length === 0}>
						{allOpen ? 'סגור הכל' : 'פתח הכל'}
					</Button>
				</Stack>
				<TableContainer>
					<Table size="small" stickyHeader>
						<TableHead>
							<TableRow>
								<TableCell sx={{ width: 40 }} />
								{showCompany && <SortHead k="company" label="חברה" />}
								<SortHead k="offered_at" label="תאריך" />
								<SortHead k="member" label="עובד" />
								<SortHead k="tier" label="סוג" />
								<SortHead k="price" label="סכום" />
								<SortHead k="status" label="סטטוס" />
								<Tooltip title="GH-727 — מבחן A/B על כותרת ההצעה. A = ״הוקצתה לך הטבה אישית בשווי מאות שקלים״, B = ״קיבלת הטבה ייחודית עבורך״. הפירוט מראה איזו גרסת אפליקציה הציגה את ההצעה.">
									<TableCell align="right" sx={{ whiteSpace: 'nowrap' }}>כותרת</TableCell>
								</Tooltip>
								<SortHead k="expert" label="מומחה" />
								<SortHead k="session_time" label="תאריך המענה" />
								<SortHead k="feedback" label="משוב" />
							</TableRow>
						</TableHead>
						<TableBody>
							{sortedRows.map((o) => {
								const memberName = memberNameOf(o);
								const expertName = o.selected_expert?.name || o.selected_expert?.english_name || '—';
								const hasText = !!(o.focus_topic || o.hr_allocation_note_he || o.offer_reason_he || o.offer_reason_en);
								const isOpen = !!open[o._id];
								return (
									<Fragment key={o._id}>
										<TableRow hover sx={{ '& > *': { borderBottom: isOpen ? 'unset' : undefined }, cursor: 'pointer' }} onClick={() => setOpen((s) => ({ ...s, [o._id]: !s[o._id] }))}>
											<TableCell sx={{ whiteSpace: 'nowrap' }}>
												<IconButton size="small" aria-label="פרטים">
													{isOpen ? <FaChevronUp size={12} /> : <FaChevronDown size={12} />}
												</IconButton>
												{hasText && !isOpen && (
													<Tooltip title="יש טקסט לקריאה (סיבה / ניסוח / נושא)">
														<Box component="span" sx={{ fontSize: 12 }}>💬</Box>
													</Tooltip>
												)}
											</TableCell>
											{showCompany && (
												<TableCell align="right" sx={{ whiteSpace: 'nowrap', fontWeight: 600 }}>
													{typeof o.company === 'object' && o.company ? o.company.english_name || '—' : '—'}
												</TableCell>
											)}
											<TableCell align="right" sx={{ whiteSpace: 'nowrap' }}>{fmtDate(o.offered_at)}</TableCell>
											<TableCell align="right" sx={{ whiteSpace: 'nowrap' }}>{memberName}</TableCell>
											<TableCell align="right" sx={{ whiteSpace: 'nowrap' }}>
												{o.continuation_of ? 'המשך טיפול' : TIER_LABELS[o.intervention_category]}
												{o.adaptive_track && !o.continuation_of ? (
													<Chip size="small" label="פוטנציאל המשך" sx={{ mr: 0.5, bgcolor: '#EDE9FE', color: '#5B21B6', fontSize: 11, height: 20 }} />
												) : null}
											</TableCell>
											<TableCell align="right">₪{o.price}</TableCell>
											<TableCell align="right">
												<Chip size="small" label={STATUS_LABELS[o.status]} color={STATUS_COLORS[o.status]} />
											</TableCell>
											<TableCell align="right">
												{o.copy_variant ? (
													<Chip
														size="small"
														label={o.copy_variant}
														sx={{ fontWeight: 700, bgcolor: o.copy_variant === 'B' ? '#FEF3C7' : '#E0F2FE', color: '#00224B' }}
													/>
												) : (
													'—'
												)}
												{o.is_demo && <DemoChip />}
											</TableCell>
											<TableCell align="right" sx={{ whiteSpace: 'nowrap' }}>{expertName}</TableCell>
											<TableCell align="right" sx={{ whiteSpace: 'nowrap' }}>{fmtDateTime(o.session_time)}</TableCell>
											<TableCell align="right" onClick={(e) => e.stopPropagation()}>{feedbackCell(o)}</TableCell>
										</TableRow>
										<TableRow>
											<TableCell colSpan={compactColSpan} sx={{ py: 0, borderBottom: isOpen ? undefined : 'unset' }}>
												<Collapse in={isOpen} timeout="auto" unmountOnExit>
													<Box sx={{ py: 2, px: 1, bgcolor: '#FAFAFA' }}>
														<Grid container spacing={2}>
															<Grid item xs={6} md={2}><Field label="אימייל">{o.member?.email}</Field></Grid>
															<Grid item xs={6} md={2}><Field label="מחלקה">{typeof o.member?.department === 'string' ? o.member.department : undefined}</Field></Grid>
															<Grid item xs={4} md={1}><Field label="ציון צורך">{o.need_score_at_offer?.toFixed(1)}</Field></Grid>
															<Grid item xs={4} md={2}><Field label="תזכורת נשלחה">{o.reminder_sent_at ? `✓ ${fmtDate(o.reminder_sent_at)}` : undefined}</Field></Grid>
															<Grid item xs={4} md={2}>
																<Field label="לחץ על הלינק">
																	{o.clicked_at ? `✓ ${fmtDate(o.clicked_at)}${o.click_channel ? ` · ${CHANNEL_LABELS[o.click_channel] ?? ''}` : ''}` : undefined}
																</Field>
															</Grid>
															<Grid item xs={6} md={2}><Field label="הועבר מ־">{reallocatedLabel(o)}</Field></Grid>
															<Grid item xs={6} md={1}><Field label="תוקף עד">{fmtDate(o.offer_expires_at)}</Field></Grid>
															{o.copy_variant && (
																<Grid item xs={6} md={2}>
																	<Field label={`כותרת (${o.copy_experiment || 'A/B'})`}>
																		{`${o.copy_variant}${o.shown_platform || o.shown_app_version ? ` · הוצג ב-${o.shown_platform || ''} ${o.shown_app_version || ''}`.trimEnd() : ' · טרם נפתח באפליקציה/אתר'}`}
																	</Field>
																</Grid>
															)}
															{(o.offer_reason_he || o.offer_reason_en) && (
																<Grid item xs={12} md={4}>
																	<Field label={`סיבת ההצעה${o.offer_reason_expert ? ` — ${o.offer_reason_expert}` : ''}${o.offer_reason_source === 'selected' ? ' (נבחר)' : o.offer_reason_source === 'recommended' ? ' (מומלץ)' : ''}`}>
																		{o.offer_reason_he || o.offer_reason_en}
																	</Field>
																</Grid>
															)}
															{o.hr_allocation_note_he && (
																<Grid item xs={12} md={4}><Field label="הצעת ניסוח ל-HR">{o.hr_allocation_note_he}</Field></Grid>
															)}
															{o.focus_topic && (
																<Grid item xs={12} md={4}><Field label="נושא מהמנוי (לא מוצג ל-HR)">💬 {o.focus_topic}</Field></Grid>
															)}
														</Grid>
													</Box>
												</Collapse>
											</TableCell>
										</TableRow>
									</Fragment>
								);
							})}
							{rows.length === 0 && (
								<TableRow>
									<TableCell colSpan={compactColSpan} align="center">
										<Typography variant="body2" color="text.secondary" sx={{ py: 2 }}>
											{emptyText}
										</Typography>
									</TableCell>
								</TableRow>
							)}
						</TableBody>
					</Table>
				</TableContainer>
				{maxRows && offers.length > maxRows && (
					<Box sx={{ mt: 1, textAlign: 'center' }}>
						<Typography variant="caption" color="text.secondary">
							מציג {maxRows} ראשונים מתוך {offers.length}
						</Typography>
					</Box>
				)}
				{feedbackDialog}
			</>
		);
	}

	return (
		<>
					<TableContainer>
						<Table size="small">
							<TableHead>
								<TableRow>
									{showCompany && <TableCell align="right">חברה</TableCell>}
									<TableCell align="right">תאריך</TableCell>
									<TableCell align="right">עובד</TableCell>
									<TableCell align="right">אימייל</TableCell>
									<TableCell align="right">מחלקה</TableCell>
									<TableCell align="right">סוג</TableCell>
									<TableCell align="right">סכום</TableCell>
									<TableCell align="right">ציון צורך</TableCell>
									<TableCell align="right">סטטוס</TableCell>
									<TableCell align="right">משוב</TableCell>
									<TableCell align="right">תזכורת נשלחה</TableCell>
									<TableCell align="right">לחץ על הלינק</TableCell>
									<TableCell align="right">מומחה שנבחר</TableCell>
									<TableCell align="right">תאריך המענה</TableCell>
									<TableCell align="right">הועבר מ־</TableCell>
									<TableCell align="right">סיבת ההצעה</TableCell>
									<TableCell align="right">הצעת ניסוח ל-HR</TableCell>
									<TableCell align="right">נושא מהמנוי</TableCell>
								</TableRow>
							</TableHead>
							<TableBody>
								{rows.map((o) => {
									const memberName = o.member
										? `${o.member.first_name ?? ''} ${o.member.last_name ?? ''}`.trim() || '—'
										: '—';
									const expertName = o.selected_expert?.name || o.selected_expert?.english_name || '—';
									const prevMember = o.reallocated_from?.member;
									const reallocatedFrom = prevMember
										? `${prevMember.first_name ?? ''} ${prevMember.last_name ?? ''}`.trim() ||
											'הצעה קודמת'
										: o.reallocated_from
											? 'הצעה קודמת'
											: null;
									return (
										<TableRow key={o._id}>
											{showCompany && (
												<TableCell align="right" sx={{ whiteSpace: 'nowrap', fontWeight: 600 }}>
													{typeof o.company === 'object' && o.company ? o.company.english_name || '—' : '—'}
												</TableCell>
											)}
											<TableCell align="right">
												{new Date(o.offered_at).toLocaleDateString('he-IL')}
											</TableCell>
											<TableCell align="right">{memberName}</TableCell>
											<TableCell align="right">{o.member?.email ?? '—'}</TableCell>
											<TableCell align="right">
												{typeof o.member?.department === 'string' ? o.member.department : '—'}
											</TableCell>
											<TableCell align="right">
												{o.continuation_of ? 'המשך טיפול' : TIER_LABELS[o.intervention_category]}
												{o.adaptive_track && !o.continuation_of ? (
													<Chip
														size="small"
														label="פוטנציאל המשך"
														sx={{ mr: 0.5, bgcolor: '#EDE9FE', color: '#5B21B6', fontSize: 11, height: 20 }}
													/>
												) : null}
											</TableCell>
											<TableCell align="right">₪{o.price}</TableCell>
											<TableCell align="right">{o.need_score_at_offer?.toFixed(1) ?? '—'}</TableCell>
											<TableCell align="right">
												<Chip
													size="small"
													label={STATUS_LABELS[o.status]}
													color={STATUS_COLORS[o.status]}
												/>
												{o.is_demo && <DemoChip />}
											</TableCell>
											<TableCell align="right">
												{o.feedback && o.feedback.length > 0 ? (
													<Tooltip title="צפייה במשוב">
														<Chip
															size="small"
															color="secondary"
															clickable
															onClick={() =>
																setFeedbackView({ name: memberName, items: o.feedback!, expert: o.expert_feedback })
															}
															label={`★ ${o.feedback[0].rating ?? '—'}/5${
																o.feedback.length > 1 ? ` (+${o.feedback.length - 1})` : ''
															}`}
														/>
													</Tooltip>
												) : o.awaiting_feedback ? (
													<Tooltip
														title={
															o.feedback_reminder_sent_at
																? 'הפגישה התקיימה, המנוי לא מילא משוב, ותזכורת אוטומטית (48 שעות) כבר נשלחה — הצגת קישור למשוב'
																: 'הפגישה התקיימה — הצגת קישור למשוב'
														}
													>
														<Chip
															size="small"
															color="warning"
															variant="outlined"
															clickable
															onClick={() =>
																setFeedbackView({
																	name: memberName,
																	link: o.feedback_link ?? null,
																	expert: o.expert_feedback,
																})
															}
															label={
																o.feedback_reminder_sent_at
																	? `לא השאיר משוב · תזכורת ${new Date(o.feedback_reminder_sent_at).toLocaleDateString('he-IL')}`
																	: 'לא השאיר משוב'
															}
														/>
													</Tooltip>
												) : o.expert_feedback ? null : (
													'—'
												)}
												{expertChip(o)}
											</TableCell>
											<TableCell align="right">
												{o.reminder_sent_at ? (
													<Chip
														size="small"
														variant="outlined"
														color="info"
														label={`✓ ${new Date(o.reminder_sent_at).toLocaleDateString('he-IL')}`}
													/>
												) : (
													'—'
												)}
											</TableCell>
											<TableCell align="right">
												{o.clicked_at ? (
													<Chip
														size="small"
														variant="outlined"
														color="success"
														label={`✓ ${new Date(o.clicked_at).toLocaleDateString('he-IL')}${
															({ em: ' · מייל', wa: ' · וואטסאפ', em_rem: ' · מייל תזכורת', wa_rem: ' · וואטסאפ תזכורת' } as any)[
																o.click_channel ?? ''
															] ?? ''
														}`}
													/>
												) : (
													'—'
												)}
											</TableCell>
											<TableCell align="right">{expertName}</TableCell>
											{/* "תאריך המענה" (Udi, 8.9.2026) — the date/time the session was scheduled for */}
											<TableCell align="right" sx={{ whiteSpace: 'nowrap' }}>
												{o.session_time
													? new Date(o.session_time).toLocaleString('he-IL', {
															day: 'numeric',
															month: 'numeric',
															year: 'numeric',
															hour: '2-digit',
															minute: '2-digit',
															hour12: false,
															timeZone: 'Asia/Jerusalem',
													  })
													: '—'}
											</TableCell>
											<TableCell align="right">
												{reallocatedFrom ? (
													<Chip
														size="small"
														variant="outlined"
														color="warning"
														label={`↩ ${reallocatedFrom}${
															(o.reallocation_depth ?? 0) > 1 ? ` (${o.reallocation_depth})` : ''
														}`}
													/>
												) : (
													'—'
												)}
											</TableCell>
											<TableCell align="right">
												{o.offer_reason_he || o.offer_reason_en ? (
													<Tooltip title="למה המומחה הזה הוצע (נימוק ההתאמה)">
														<Chip
															size="small"
															color="info"
															variant="outlined"
															clickable
															onClick={() =>
																setNoteView({
																	name: memberName,
																	he: o.offer_reason_he,
																	en: o.offer_reason_en,
																	expert: o.offer_reason_expert,
																	source: o.offer_reason_source,
																})
															}
															label={o.offer_reason_source === 'selected' ? 'סיבה ✓' : 'סיבה'}
														/>
													</Tooltip>
												) : (
													'—'
												)}
											</TableCell>
											<TableCell align="right">
												{o.hr_allocation_note_he ? (
													<Typography variant="body2" color="text.secondary" sx={{ maxWidth: 300, whiteSpace: 'normal' }}>
														{o.hr_allocation_note_he}
													</Typography>
												) : (
													'—'
												)}
											</TableCell>
											<TableCell align="right">
												{o.focus_topic ? (
													<Tooltip title="מה המנוי כתב שירצה להתמקד בו — נשלח גם למומחה במייל. לא מוצג ל-HR.">
														<Typography variant="body2" sx={{ maxWidth: 260, whiteSpace: 'normal' }}>
															💬 {o.focus_topic}
														</Typography>
													</Tooltip>
												) : (
													'—'
												)}
											</TableCell>
										</TableRow>
									);
								})}
								{rows.length === 0 && (
									<TableRow>
										<TableCell colSpan={colSpan} align="center">
											<Typography variant="body2" color="text.secondary" sx={{ py: 2 }}>
												{emptyText}
											</Typography>
										</TableCell>
									</TableRow>
								)}
							</TableBody>
						</Table>
					</TableContainer>
					{maxRows && offers.length > maxRows && (
						<Box sx={{ mt: 1, textAlign: 'center' }}>
							<Typography variant="caption" color="text.secondary">
								מציג {maxRows} ראשונים מתוך {offers.length}
							</Typography>
						</Box>
					)}
			{feedbackDialog}
		</>
	);
};

export default SubsidyOffersTable;
